'use client'
import { Box, Flex, NumberInput, NumberInputProps, Stack, Text } from '@mantine/core'
import { useQueryClient } from '@tanstack/react-query'

import { useStockContext } from '@/app/[locale]/price/stocks/[symbol]/_contexts/StockContext'
import ArrowUpDown from '@/assets/icons/arrow-up-down.svg'
import CoinIcon from '@/components/CoinIcon'
import useGlobalStore from '@/stores/global'

import FakeSelector from '../FakeSelector'
import { usePriceConvert } from './hooks'
import styles from './index.module.css'
import MoneyItems from './MoneyItems'
import { allStocksQueryOptions } from './StockListModal/hooks'

const TO_VALUE_VARIANT = 'body-emphasis'

export default function PriceConvertor({ openCoinListModal }: { openCoinListModal: () => void }) {
  const {
    fromValue,
    toValue,
    fromSymbol,
    toSymbol,
    onInputValue,
    toggleReversed,
    setCurrencyValue,
  } = usePriceConvert()

  const queryClient = useQueryClient()
  const { stock } = useStockContext()
  const allCoinsStatus = useGlobalStore(state => state.allCoinsStatus)
  const isLoading = allCoinsStatus === 'pending' || allCoinsStatus === undefined
  const onFocus: NumberInputProps['onFocus'] = ev => {
    // Move cursor to the end of the input when focused
    const input = ev.target
    const valueLength = input.value.length
    ev.target.setSelectionRange(valueLength, valueLength)
    input.scrollLeft = input.scrollWidth
  }

  const handlePrefetch = () => {
    queryClient.prefetchQuery(allStocksQueryOptions)
  }

  return (
    <Stack gap={12}>
      <Box className={styles.priceConvertor}>
        <Flex className={styles.fromContainer}>
          <Flex className={styles.inputContainer}>
            <Flex className={styles.fromNumberContainer}>
              <NumberInput
                variant={'unstyled'}
                classNames={{
                  input: styles.fromNumber,
                }}
                value={fromValue}
                hideControls={true}
                onChange={onInputValue}
                onFocus={onFocus}
              />
              <Text style={{ visibility: 'hidden' }} className={styles.fromNumber}>
                {fromSymbol}
              </Text>
              <Flex className={styles.currencyContainer}>
                <Text
                  style={{ visibility: 'hidden', overflow: 'hidden' }}
                  className={styles.fromNumber}
                >
                  {fromValue}
                </Text>
                <Text c={'var(--content-tertiary)'} className={styles.fromNumber}>
                  {fromSymbol}
                </Text>
              </Flex>
            </Flex>
          </Flex>
          <FakeSelector
            bg={'var(--bg-surface-primary)'}
            onClick={openCoinListModal}
            onMouseEnter={handlePrefetch}
            content={
              <Flex align={'center'}>
                <Box bdrs={888} bg={'var(--content-primary)'}>
                  {isLoading ? <Box w={20} h={20} /> : <CoinIcon coin={stock} />}
                </Box>
                <Text variant={'subtitle'} ml={8}>
                  {stock.symbol}
                </Text>
              </Flex>
            }
          />
        </Flex>
        <Flex mt={10} align={'center'}>
          <Text c={'var(--content-tertiary)'} variant={TO_VALUE_VARIANT}>
            ≈
          </Text>
          <Text mx={4} variant={TO_VALUE_VARIANT}>
            {toValue}
          </Text>
          <Text mr={4} c={'var(--content-tertiary)'} variant={TO_VALUE_VARIANT}>
            {toSymbol}
          </Text>
          <ArrowUpDown onClick={toggleReversed} className={styles.arrowUpDown} />
        </Flex>
      </Box>
      <MoneyItems setCurrencyValue={setCurrencyValue} />
    </Stack>
  )
}
